/**
 * Build batched edit-campaign requests from the review grid's pending edits.
 *
 * The CLW review grid edits the plan TEMPLATE through whole-document PUTs
 * (PUT /planning-review / gt-review-edits). That only works before launch —
 * once a campaign is live the backend refuses the PUT ("not in planning review
 * mode") and, even if it didn't, re-writing the plan document would not change
 * the live platform campaign.
 *
 * This module is the launched-campaign half of the save model. It converts the
 * same accumulated edit state (`editedRows` + `pendingListOps`) into one or more
 * free-form `POST /api/v2/campaigns/:id/edit` requests, each carrying a plain
 * English `description` plus the platform entity ids to target. The Python
 * editor's per-platform reasoning then parses the description and mutates the
 * live campaign — the same free-form field-editing path the optimizer's
 * "apply suggestion" flow already drives (see utils/optimizerSuggestionApply.js).
 *
 * One request is emitted per edited ROW (line item / ad group / asset group),
 * with that row's field changes folded into a single description. Fields are
 * addressed by their human column label so the description reads the way a
 * reviewer would say it, and `targetEntities` carries the row's platform id
 * where the row builder published one (DV360 lineItemId, TTD adGroupId).
 * Google Ads rows are positional (`gads-<ci>-<ai>`) with no platform id in the
 * plan, so those requests target the campaign and name the entity in prose.
 */

import { GROUP_COLS } from './clwGridConstants';
import { gadsSubtype, gadsGroupCols } from './gads/gadsConstants';
import { GT_GROUP_COLS } from './groundtruth/gtConstants';
import { isFbPlatform, FB_GROUP_COLS } from './fb/fbConstants';

/** Grid field key → human column label, for prose descriptions. */
function fieldLabel(key, family, subtype) {
  // List-cell sub-fields are staged under keys that are not grid columns
  // (geoInclude / geoExclude); name them with the human column label so both
  // prose and the structured op read naturally. The GADS grid calls that
  // column "Locations"; DV360/TTD call it "Geography".
  if (key === 'geoInclude' || key === 'geoExclude') return family === 'gads' ? 'Locations' : 'Geography';
  // Max CPC left the visible column set (a meaningless floor on automated
  // bidding), but the FIELD stays editable through the agent/edit paths — its
  // prose label must not degrade to the raw key.
  if (key === 'bidFloor' && family === 'gads') return 'Max CPC';
  if (family === 'gads') {
    const cols = Object.values(gadsGroupCols(subtype) || {});
    for (const g of cols) for (const c of g) if (c.k === key) return c.l;
    return key;
  }
  if (family === 'fb') {
    const cols = Object.values(FB_GROUP_COLS || {});
    for (const g of cols) for (const c of g) if (c.k === key) return c.l;
    return key;
  }
  if (family === 'gt') {
    const cols = Object.values(GT_GROUP_COLS || {});
    for (const g of cols) for (const c of g) if (c.k === key) return c.l;
    return key;
  }
  for (const g of Object.values(GROUP_COLS)) for (const c of g) if (c.k === key) return c.l;
  return key;
}

/** Which platform family (dv360 | ttd | gads | gt | fb) a destination maps to. */
function familyFor(destination) {
  const d = String(destination || '').toLowerCase();
  if (isFbPlatform(d)) return 'fb';
  if (d === 'thetradedesk' || d === 'ttd') return 'ttd';
  if (d === 'groundtruth' || d === 'gt') return 'gt';
  if (gadsSubtype(d)) return 'gads';
  return 'dv360';
}

/** The platform entity id a review row publishes, or null. */
function rowEntityId(row, family) {
  if (family === 'dv360') {
    // rowId is the line item id (dv360RowBuilders uses li.lineItemId).
    return /^li-\d+$/.test(String(row?.rowId || '')) ? null : row?.rowId || null;
  }
  if (family === 'ttd') {
    // rowId is the ad group id (ttdRowBuilders uses adGroupId), fallback ag-<i>.
    return /^ag-\d+$/.test(String(row?.rowId || '')) ? null : row?.rowId || null;
  }
  if (family === 'gt') {
    // GT rows publish the platform ad-group id when the launch result carried
    // one (gtRowBuilders publishes ag.adgroup_id); the positional gt-ag-<i>
    // rowId alone can't reach the GT API.
    return row?.adgroup_id || null;
  }
  if (family === 'fb') {
    // Imported FB plans carry the source ad-set id on the row; pre-launch
    // templates usually don't, so that case degrades to the old campaign-
    // level prose path exactly as before.
    return row?.adset_id || row?.ad_set_id || null;
  }
  // gads row ids are positional (`gads-<ci>-<ai>`) and carry no platform id —
  // the edit targets the campaign and names the entity in prose.
  return null;
}

/** The noun naming one row's entity, for prose ("line item", "ad group", …). */
function entityNoun(family, subtype) {
  if (family === 'gads') return subtype === 'search' ? 'ad group' : 'asset group';
  if (family === 'ttd' || family === 'gt') return 'ad group';
  if (family === 'fb') return 'ad set';
  return 'line item';
}

/** Prose for one staged list op against a field. */
function listOpClause(op, fieldLabelText) {
  if (op.op === 'edit') return `change "${op.value}" to "${op.newValue}" in ${fieldLabelText}`;
  if (op.op === 'add') return `add "${op.value}" to ${fieldLabelText}`;
  if (op.op === 'remove') return `remove "${op.value}" from ${fieldLabelText}`;
  return null;
}

/**
 * Convert pending grid edits into batched edit-campaign request descriptors.
 *
 * Each descriptor keeps the free-form `description` + `targetEntities` shape
 * the single-edit chat path has always consumed, and adds a STRUCTURED `ops`
 * list (`field`, `op`, `value`, `newValue`, `label`) plus the row context the
 * Python editor needs to apply the edit exactly (entity noun, sibling count,
 * row id). The launched-edit save path sends the structured ops through the
 * metadata `config_payload` (which bypasses the LLM message analyzer) so the
 * Python campaign editor can apply the same field semantics the pre-launch
 * grid appliers write — no prose re-interpretation of money, dates, or enum
 * values.
 *
 * @param {Object} args
 * @param {Object} args.editedRows    { rowId: { field: value } }
 * @param {Object} args.pendingListOps { rowId: { field: [{ op, value, newValue? }] } }
 * @param {Array}  args.reviewRows    the grid's row array (rowId → name)
 * @param {string} args.destination   platform id / destination
 * @returns {Array<{
 *   description: string,
 *   targetEntities: string[],
 *   family: 'dv360'|'ttd'|'gads'|'gt'|'fb',
 *   subtype: string|null,
 *   rowId: string,
 *   entityId: string|null,
 *   entityNoun: string,
 *   entityName: string|null,
 *   ops: Array<{field,label,op,value,newValue?}>
 * }>}
 */
export function buildLaunchedEditRequests({ editedRows = {}, pendingListOps = {}, reviewRows = [], destination }) {
  const family = familyFor(destination);
  const subtype = family === 'gads' ? gadsSubtype(destination) : null;
  const rowById = new Map((reviewRows || []).map((r) => [String(r?.rowId), r]));

  // Which rows have any pending change (scalar or list).
  const rowIds = new Set([
    ...Object.keys(editedRows || {}),
    ...Object.keys(pendingListOps || {}),
  ]);

  const requests = [];
  for (const rowId of rowIds) {
    // GT campaign-card edits ride editedRows under a sentinel row id
    // (GT_CAMPAIGN_ROW_ID in reviewEdits.js). They have no grid row and no
    // live-edit path — building a request for one would describe a campaign
    // field as an ad-group edit with no target entity. Unreachable today
    // (campaign editing is disabled once launched), guarded so a future
    // change can't ship the bogus request silently.
    if (rowId === 'gt-campaign') continue;
    const row = rowById.get(String(rowId));
    const clauses = [];
    const ops = [];

    const scalarFields = editedRows?.[rowId] || {};
    for (const [field, value] of Object.entries(scalarFields)) {
      const label = fieldLabel(field, family, subtype);
      clauses.push(`set ${label} to "${value}"`);
      // oldValue = the row's current value — the backend stores it so the
      // change can be undone (the inverse edit re-applies it).
      ops.push({ field, label, op: 'set', value, oldValue: row?.[field] ?? null });
    }

    const listFields = pendingListOps?.[rowId] || {};
    for (const [field, listOps] of Object.entries(listFields)) {
      const label = fieldLabel(field, family, subtype);
      for (const op of listOps || []) {
        const clause = listOpClause(op, label);
        if (!clause) continue;
        clauses.push(clause);
        ops.push({
          field, label, op: op.op, value: op.value,
          ...(op.op === 'edit' ? { newValue: op.newValue, oldValue: op.value } : {}),
        });
      }
    }

    if (clauses.length === 0) continue;

    const entityId = rowEntityId(row, family);
    const noun = entityNoun(family, subtype);
    const name = row?.name ? ` "${row.name}"` : '';
    const description = `${clauses.join('; ')} on ${noun}${name}`;
    requests.push({
      description,
      targetEntities: entityId ? [entityId] : [],
      family,
      subtype,
      rowId: String(row?.rowId ?? rowId),
      entityId,
      entityNoun: noun,
      entityName: row?.name || null,
      // Row context for exact scaling / derivation (siblingCount is published
      // by the row builders; cpm matters for DV360's derived budget cell).
      // campaignIdx lets the batch dispatcher target the correct campaign in
      // a multi-campaign Google Ads plan whose platformCampaignId is the
      // comma-joined id list.
      rowContext: row ? {
        name: row.name,
        siblingCount: row.siblingCount ?? null,
        cpm: row.cpm ?? null,
        market: row.market ?? null,
        campaignIdx: row.__campaignIdx ?? null,
      } : null,
      ops,
    });
  }

  return requests;
}

export default { buildLaunchedEditRequests };
